import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { useCallback, useMemo, useRef, useState } from 'react'

export interface UseTurnstileReturn {
  TurnstileWidget: JSX.Element
  isReady: boolean
  verify: () => Promise<{ success: boolean; error?: string; errors?: string[] }>
  reset: () => void
}

interface UseTurnstileOptions {
  action?: string
  onSuccess?: () => void
  onError?: (msg: string) => void
  appearance?: 'interaction-only' | 'always' | 'execute'
}

export const useTurnstile = (
  options?: UseTurnstileOptions
): UseTurnstileReturn => {
  const [isReady, setIsReady] = useState(false)
  const tokenRef = useRef<string | null>(null)
  const turnstileRef = useRef<TurnstileInstance>(null)

  const reset = useCallback(() => {
    turnstileRef.current?.reset()
    tokenRef.current = null
    setIsReady(false)
  }, [])

  const verify = useCallback(async () => {
    const token = tokenRef.current
    if (!token) {
      return { success: false, error: 'Token not found' }
    }

    try {
      const response = await fetch('/api/verify-tt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })

      const result = await response.json()
      return result as { success: boolean; error?: string; errors?: string[] }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Verification failed',
      }
    }
  }, [])

  const TurnstileWidget = useMemo(() => {
    return (
      <Turnstile
        ref={turnstileRef}
        siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
        options={{
          action: options?.action,
          appearance: options?.appearance || 'interaction-only',
        }}
        onSuccess={(token: string) => {
          tokenRef.current = token
          setIsReady(true)
          options?.onSuccess?.()
        }}
        onError={() => {
          const msg = 'Turnstile verification failed'
          options?.onError?.(msg)
        }}
      />
    )
  }, [options])

  return {
    TurnstileWidget,
    isReady,
    verify,
    reset,
  }
}
